import type { ContactOrderStep } from "../../../../../../../../../../api/kehrblatt/contact-orders"
import type { ContactOrderPlanRow } from "../../../../../_logic/communication-plan-table-contract"

const normalizeStatus = (status?: string | null) => (status || "").toUpperCase()

const terminalStatuses = new Set(["DONE", "CANCELED"])
const cancelProgressionStatuses = new Set(["READY", "BLOCKED", "ERROR", "RETRY_SCHEDULED"])

export type StepProgressionActionKind = "cancel" | "skip_wait"

export type StepProgressionAction = {
  kind: StepProgressionActionKind
  icon: "cancel" | "timer_off"
  tooltip: string
  successMessage: string
  errorMessage: string
}

export const isOutstandingStatus = (status?: string | null) =>
  ["READY", "RETRY_SCHEDULED", "ERROR"].includes(normalizeStatus(status))

export const canMarkLetterWaiting = (status?: string | null) =>
  !["WAITING", "DONE", "CANCELED", "BLOCKED"].includes(normalizeStatus(status))

export const isCancelableStatus = (status?: string | null) =>
  cancelProgressionStatuses.has(normalizeStatus(status))

export const isStepEditable = (status?: string | null) =>
  !["CANCELED"].includes(normalizeStatus(status))

export const isTerminalStepStatus = (status?: string | null) =>
  terminalStatuses.has(normalizeStatus(status))

export const resolveStepProgressionAction = (
  step: ContactOrderStep | null,
  stepId?: string | null,
): StepProgressionAction | null => {
  if (!stepId || !step) return null

  const status = normalizeStatus(step.status)

  if (status === "WAITING") {
    return {
      kind: "skip_wait",
      icon: "timer_off",
      tooltip: "Wartezeit beenden und nächsten Kontaktschritt freigeben",
      successMessage: "Wartezeit beendet. Der nächste Schritt kann jetzt ausgeführt werden.",
      errorMessage: "Wartezeit konnte nicht beendet werden.",
    }
  }

  if (cancelProgressionStatuses.has(status)) {
    return {
      kind: "cancel",
      icon: "cancel",
      tooltip: "Diesen geplanten Kontaktschritt überspringen",
      successMessage: "Kontaktschritt übersprungen.",
      errorMessage: "Kontaktschritt konnte nicht übersprungen werden.",
    }
  }

  return null
}

export const computeNextDueAt = (steps: ContactOrderStep[] = []) => {
  const timestamps: number[] = []
  for (const step of steps) {
    const ts = step.due_at ? new Date(step.due_at).getTime() : NaN

    if (!Number.isNaN(ts)) timestamps.push(ts)
  }
  const latest = timestamps.length ? Math.max(...timestamps) : Date.now()
  const base = Math.max(latest, Date.now())

  return new Date(base + 60_000).toISOString()
}

export const isRetryableStep = (row: ContactOrderPlanRow, step: ContactOrderStep | null) => {
  if (!step) return false

  const channel = normalizeStatus(step.channel || row.channel)

  if (channel === "LETTER") return false

  const status = normalizeStatus(step.status)

  if (["DONE", "WAITING"].includes(status)) return false

  const due = step.due_at ? new Date(step.due_at).getTime() : NaN

  return Number.isNaN(due) ? true : due <= Date.now()
}

export const blockedTargetTooltip =
  "Dieses Kontaktziel ist gesperrt und kann nicht ausgeführt werden. Bitte Schritt abbrechen oder Kontaktziel ändern."
